import {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { useAuth } from "@/hooks/use-auth";
import {
  canUseBreakGlass,
  clearBreakGlassSession,
  PLAC_DENIED_EVENT,
  readBreakGlassSession,
  writeBreakGlassSession,
  type BreakGlassSession,
  type PlacDeniedDetail,
} from "@/lib/break-glass";
import { BreakGlassModal } from "@/components/break-glass/break-glass-modal";
import { BreakGlassDeniedDialog } from "@/components/break-glass/break-glass-denied-dialog";

export interface BreakGlassContextValue {
  session: BreakGlassSession | null;
  isActive: boolean;
  eligible: boolean;
  activate: (reason: string) => void;
  deactivate: () => void;
  openActivationModal: () => void;
  remainingMs: number;
}

export const BreakGlassContext = createContext<BreakGlassContextValue | null>(null);

interface BreakGlassProviderProps {
  children: ReactNode;
}

export function BreakGlassProvider({ children }: BreakGlassProviderProps) {
  const { user } = useAuth();
  const eligible = canUseBreakGlass(user);
  const [session, setSession] = useState<BreakGlassSession | null>(() =>
    readBreakGlassSession(),
  );
  const [activationOpen, setActivationOpen] = useState(false);
  const [deniedDetail, setDeniedDetail] = useState<PlacDeniedDetail | null>(null);
  const [tick, setTick] = useState(0);


  useEffect(() => {
    const interval = window.setInterval(() => {
      const current = readBreakGlassSession();
      setSession(current);
      setTick((t) => t + 1);
    }, 30_000);
    return () => window.clearInterval(interval);
  }, []);

  useEffect(() => {
    if (!eligible && session) {
      clearBreakGlassSession();
      setSession(null);
    }
  }, [eligible, session]);

  useEffect(() => {
    const handler = (event: Event) => {
      if (!eligible || session) return;
      const detail = (event as CustomEvent<PlacDeniedDetail>).detail;
      setDeniedDetail(detail);
    };
    window.addEventListener(PLAC_DENIED_EVENT, handler);
    return () => window.removeEventListener(PLAC_DENIED_EVENT, handler);
  }, [eligible, session]);

  const activate = useCallback(
    (reason: string) => {
      if (!eligible) return;
      const next = writeBreakGlassSession(reason);
      setSession(next);
      setActivationOpen(false);
      setDeniedDetail(null);
    },
    [eligible],
  );

  const deactivate = useCallback(() => {
    clearBreakGlassSession();
    setSession(null);
  }, []);

  const remainingMs = useMemo(() => {
    if (!session) return 0;
    void tick;
    return Math.max(0, session.expiresAt - Date.now());
  }, [session, tick]);

  const value = useMemo<BreakGlassContextValue>(
    () => ({
      session,
      isActive: !!session && remainingMs > 0,
      eligible,
      activate,
      deactivate,
      openActivationModal: () => setActivationOpen(true),
      remainingMs,
    }),
    [session, remainingMs, eligible, activate, deactivate],
  );

  return (
    <BreakGlassContext.Provider value={value}>
      {children}
      {eligible && (
        <>
          <BreakGlassModal
            open={activationOpen}
            onOpenChange={setActivationOpen}
            onConfirm={activate}
          />
          <BreakGlassDeniedDialog
            detail={deniedDetail}
            onOpenChange={(open) => {
              if (!open) setDeniedDetail(null);
            }}
            onActivate={(reason) => {
              activate(reason);
              setDeniedDetail(null);
            }}
          />
        </>
      )}
    </BreakGlassContext.Provider>
  );
}
